import React, { useEffect, useRef } from "react";
import Button from "../../models/Button";
import ButtonChannel from "../../models/ButtonChannel";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";

import OneButton, { ButtonChannelErrorRow } from "./OneButton";

import Logger from "../../Logger";
import { ChannelType, specialChannelTypes } from "../../models/ChannelType";
import { useRedraw } from "../../models/useRerender";
import { itx } from "../../translationItx/itx";
import { sortArrayByProperty } from "../../models/utils";
import Channel from "../../models/Channel";
import type Device from "../../models/Device";
import Actor from "../../models/Actor";
import { TAG_TX } from "../../models/Constants";
import { allignColumnTreeview } from "../../modelViews/GraficElementUtils";
import Signal from "../../models/Signal";
import OneSignal, { OneButtonManualSignals } from "./OneSignal";
import { isUiModeExpert } from "../../uiMode";

const logger = new Logger();

export default function KnxButtonTable(props: { button: Device }) {
  const button = props.button;

  const rootElementRef = useRef<HTMLTableElement>(null);

  async function init() {
    // const rules = button.template?.knxProductData.currentRules;
    //   if (rules) {
    //     button.channels.forEach(buttonChannel =>{
    //       rules.setRockerConfig(button, buttonChannel);
    //     })
    //     await button.updateAllFromKnxMetadata();
    //   }

    button.removeUnusedChannels_WithoutKnxChannel_And_WithoutConnections();
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (rootElementRef.current) {
      const maxWidths = 300;
      allignColumnTreeview("elm1", rootElementRef.current, maxWidths);
    }
  });

  useOnChangeWatcher(button.channels);
  useOnChangeWatcher(state.uiMode);

  const redraw = useRedraw();

  function UnMappedButtonsHeader() {
    return (
      <tr>
        <td colSpan={10} style={{ backgroundColor: "white" }}>
          <div
            style={{
              width: "100%",
              marginTop: "20px",
              marginBottom: "10px",
              backgroundColor: "var(--backgroundColorError",
              color: "var(----fontColorError",
              padding: "10px",
              paddingLeft: "20px",
            }}
          >
            {state.translate(
              "knxCombObjTable.noRealisationInDevice",
              "Für die folgenden Funktionen gibt keine Realisierungen in diesem Gerät.",
            )}
            <br />
            {state.translate(
              "knxCombObjTable.instructionHelp",
              "Bitte änderen Sie die Parameter, oder verschieben Sie diese Funktionen zu einem anderen Gerät",
            )}
          </div>
        </td>
      </tr>
    );
  }

  function HeaderRow(props: { text: string }) {
    const { text } = props;

    return (
      <tr key={`knxSeparator`}>
        <td colSpan={10}>
          <div
            style={{
              borderTopLeftRadius: "10px",
              borderTopRightRadius: "10px",
              marginLeft: "-5px",
              marginTop: "20px",
              marginBottom: "0px",
              padding: "5px",
              paddingLeft: "20px",
              height: "34px",
              width: "calc(100% + 10px)",
              backgroundColor: "#666",
              color: "white",
            }}
          >
            {text}
          </div>
        </td>
      </tr>
    );
  }

  function collectionToRows(button: Device, channels: Array<Channel>): Array<any> {
    const elementsRows: any = [];

    var lastButtonChannel: Channel | undefined = channels[0];

    channels = channels.filter((channel) => !specialChannelTypes.includes(channel.channelType));

    for (let i = 0; i < channels.length; i++) {
      const buttonChannel = channels[i];
      const nextButtonChannel: Channel | undefined = i < channels.length - 1 ? channels[i + 1] : undefined;
      const previousButtonChannel: Channel | undefined = i > 0 ? channels[i - 1] : undefined;
      ///// define possibility of delete
      let withDelete = false;
      if (buttonChannel.canBeDeletedInteractive()) {
        const lastOneRight = channels[channels.length - 1].tag.includes("R");
        if (!lastOneRight && i == channels.length - 1) {
          withDelete = true;
        } else if (lastOneRight && i == channels.length - 2) {
          withDelete = true;
        }
      }

      lastButtonChannel = buttonChannel;
      elementsRows.push(
        <OneButton
          device={button}
          buttonChannel={buttonChannel}
          previousButtonChannel={previousButtonChannel}
          nextButtonChannel={nextButtonChannel}
          index={i}
          key={buttonChannel.id}
          redraw={redraw}
          withDelete={withDelete}
        />,
      );
      // add here manual connected signals
      elementsRows.push(<OneButtonManualSignals button={button} buttonChannel={buttonChannel} redraw={redraw} />);
      elementsRows.push(<ButtonChannelErrorRow buttonChannel={buttonChannel} />);
    }

    return elementsRows;
  }

  function actorChannelsToRows(button: Device, channels: Array<Channel>): Array<any> {
    const elementsRows: any = [];

    channels = channels.filter((channel) => !specialChannelTypes.includes(channel.channelType));

    for (let i = 0; i < channels.length; i++) {
      const buttonChannel = channels[i];
      elementsRows.push(
        <OneButton
          device={button}
          buttonChannel={buttonChannel as ButtonChannel}
          previousButtonChannel={undefined}
          nextButtonChannel={undefined}
          index={i}
          key={buttonChannel.id}
          redraw={redraw}
          withDelete={false}
        />,
      );

      elementsRows.push(
        <OneButtonManualSignals button={button} buttonChannel={buttonChannel as ButtonChannel} redraw={redraw} />,
      );
    }

    return elementsRows;
  }

  function AddButton(props: { button: Button }) {
    const { button } = props;

    return (
      <button
        className="button white"
        onClick={async () => {
          const buttonChannel = button.isDecentralActor
            ? button.createNewRequestActorChannel()
            : button.createNewRequestChannel();

          if (button.template) {
            // set rocker config can set disabled to rocker config
            const rules = button.template.knxProductData.currentRules;
            if (
              !button.isDecentralActor &&
              rules &&
              button.baseProps.buttonBaseProps?.supportsSplitRocker &&
              (buttonChannel as ButtonChannel).mayCallSetRockerConfig
            ) {
              rules.setRockerConfig(button, buttonChannel as ButtonChannel);
              await button.updateAllFromKnxMetadata();
            }

            //
            // Für buttons mit Customizing setzten wir den RockerType (auf 2 Tasten-Funktion)
            // das bewirkt bei zB bei dem MDT Taster das Aktivieren der Taste,
            // die dann anschließend korrekzt in der Buttonliste erscheint.
            //
            if (buttonChannel.hasKnxChannelTypeProperty) {
              buttonChannel.setKnxChannelTypePropertyValue(ChannelType.Switch, true);
            }
            await button.updateAllFromKnxMetadata();
          }

          button.channels.raiseOnChange("childCollection");
        }}
      >
        <i className="las la-plus-circle"></i>
        &nbsp;
        {button.isDecentralActor
          ? itx("knxButtonTable.addChannel", "Kanal hinzufügen")
          : itx("knxButtonTable.addButton", "Taste hinzufügen")}
      </button>
    );
  }
  function DeleteAllUnusedButtons(props: { button: Button }) {
    const { button } = props;

    const unusedButtons = button.channels.asArray().filter((c) => {
      return (
        !button.shouldPreserveUnusedChannel(c) &&
        !c.knxChannel &&
        c.connections.size == 0 &&
        c.allGroupAddressesOfChannel().length == 0
      );
    });

    if (unusedButtons.length == 0) {
      return null;
    }

    return (
      <button
        className="button white"
        style={{ margin: "5px" }}
        onClick={() => {
          unusedButtons.forEach((buttonChannel) => {});

          button.channels.raiseOnChange("childCollection");
        }}
      >
        <i className="las la-minus-circle"></i>
        &nbsp;
        {state.translate("knxButtonTable.deleteAllUnusedButtons", "Alle nicht benutzen Tasten löschen")}
      </button>
    );
  }
  logger.log(`Render KnxButtonTable`);

  const elementsHeader: any = [];

  var groupNameHeader = "";
  button.channels.forEach((buttonchannel) => {
    if (buttonchannel.connections.size > 1) {
      groupNameHeader = state.translate("knxButtonTable.groupName", "Gruppenbezeichnung");
    }
  });

  elementsHeader.push(<th></th>);
  elementsHeader.push(<th></th>);
  elementsHeader.push(<th></th>);
  elementsHeader.push(<th></th>);
  elementsHeader.push(
    <th colSpan={2} style={{ textAlign: "center" }}>
      {/* {state.translate("name", "Name")} */}
      {itx("knxButtonTable.nameButtenName", "Name / Tastenbeschriftung")}
    </th>,
  );
  // elementsHeader.push(<th>Pos</th>);
  elementsHeader.push(
    <th style={{ textAlign: "center" }}>{state.translate("knxButtonTable.buttonFunctions", "Tastenfunktion")}</th>,
  );

  // elementsHeader.push(<th style={{ textAlign: "center" }}>{groupNameHeader} </th>);

  elementsHeader.push(
    <th style={{ textAlign: "center" }}>{state.translate("components.plural.Connection", "Verbindungen")}</th>,
  );
  if (!button.isActor) {
    elementsHeader.push(
      <th style={{ textAlign: "center" }}>
        {state.translate("knxButtonTable.createConnection", "Verbindung erstellen")}
      </th>,
    );
  }

  let childChannels = new Array<ButtonChannel>();
  let childChannelsForTemperatureMessurementEtc = new Array<ButtonChannel>();
  let restChannels = new Array<ButtonChannel>();
  let errorChannels = new Array<ButtonChannel>();
  let actorChannels = new Array<Channel>();

  let logicChannels = new Array<Channel>();

  button.channelsSortedForRockerMapping().forEach((channel) => {
    if (
      channel.channelType == ChannelType.Environment ||
      channel.channelType == ChannelType.Special ||
      channel.channelType == ChannelType.ManualSignals ||
      channel.channelType == ChannelType.Ignored
    ) {
      // ignore Environment / Special / ManualSignals / Ignored channels
    } else if (!channel.knxChannel && button.template) {
      errorChannels.push(channel as ButtonChannel);
    } else if (channel.isLogicChannel) {
      logicChannels.push(channel as ButtonChannel);
    } else if (channel.isActorChannel) {
      if (channel.channelType != ChannelType.General) {
        actorChannels.push(channel as ButtonChannel);
      }
    } else if (
      !channel.isStatusChannel &&
      (channel.channelType != ChannelType.Unknown || channel.hasKnxChannelTypeProperty) &&
      channel.channelType != ChannelType.General
    ) {
      if (channel.channelType != ChannelType.TemperatureMeasurement) {
        childChannels.push(channel as ButtonChannel);
      } else {
        if (channel.channelType == ChannelType.TemperatureMeasurement) {
          childChannelsForTemperatureMessurementEtc.push(channel as ButtonChannel);
        } else {
          if (channel.tag.startsWith("T") && !channel.tag.startsWith(TAG_TX)) {
            childChannels.push(channel as ButtonChannel);
          } else {
            restChannels.push(channel as ButtonChannel);
          }
        }
      }
    }
  });

  let statusChannels = button.channels.findAsArray(
    (c) => c.isStatusChannel && c.channelType != ChannelType.Environment,
  );
  statusChannels = sortArrayByProperty(statusChannels, "tag");

  childChannels = childChannels.concat(restChannels);

  // for temperature messurement
  if (childChannels.length == 0 && childChannelsForTemperatureMessurementEtc.length > 0) {
    childChannels = childChannelsForTemperatureMessurementEtc;
    childChannelsForTemperatureMessurementEtc = new Array<ButtonChannel>();
  }

  var i = 1;

  let knxRows = collectionToRows(button, childChannels);

  if (statusChannels.length) {
    knxRows.push(<HeaderRow text={itx("KnxButtonTable.statusLeds", "Status Leds")} />);
    knxRows = knxRows.concat(collectionToRows(button, statusChannels));
  }

  //
  // Temperature Messurement
  //

  if (childChannelsForTemperatureMessurementEtc.length > 0) {
    const temperatureMessurementEtc = actorChannelsToRows(button, childChannelsForTemperatureMessurementEtc);
    knxRows.push(<HeaderRow text={itx("knxButtonTable.temperatureMessurement", "Temperaturmessung etc")} />);
    knxRows = knxRows.concat(temperatureMessurementEtc);
  }

  let showActorChannelsHeader = false;
  if (button.isButton) {
    showActorChannelsHeader = actorChannels.length > 0;
  } else {
    showActorChannelsHeader =
      actorChannels.length > 0 && (logicChannels.length > 0 || statusChannels.length > 0 || childChannels.length > 0);
  }

  if (showActorChannelsHeader) {
    knxRows.push(<HeaderRow text={itx("knxButtonTable.actorChannels", "Aktorkanäle")} />);
  }
  knxRows = knxRows.concat(actorChannelsToRows(button, actorChannels));

  if (knxRows.length == 0 && button.template) {
    knxRows.push(
      <tr>
        <td colSpan={5}>
          {state.translate(
            "knxButtonTable.noChannelsAvailable",
            "Es sind keine Kanäle vohanden. Bitte erstellen Sie unter dem Reiter Parameter welche",
          )}
        </td>
      </tr>,
    );
  }

  //
  // logic Blöcke
  //

  if (logicChannels.length) {
    for (let logicNumber = 1; logicNumber <= 9; logicNumber++) {
      let logicChannelsInput = new Array<Channel>();
      let logicChannelsOutput = new Array<Channel>();

      logicChannels.forEach((c) => {
        if (parseInt(c.tag.substring(2, 3)) == logicNumber) {
          if (c.isLogicInputChannel) {
            logicChannelsInput.push(c);
          } else if (c.isLogicOutputChannel) {
            logicChannelsOutput.push(c);
          }
        }
      });

      if (logicChannelsInput.length > 0 || logicChannelsOutput.length > 0) {
        knxRows.push(<HeaderRow text={itx("knxButtonTable.logicChannel", "Logikblock ") + logicNumber.toString()} />);
      }

      logicChannelsInput = logicChannelsInput.sort((a, b) => {
        const sa = a.tag.substring(2);
        const sb = b.tag.substring(2);
        return sa.localeCompare(sb);
      });

      logicChannelsOutput = logicChannelsOutput.sort((a, b) => {
        const sa = a.tag.substring(2);
        const sb = b.tag.substring(2);
        return sa.localeCompare(sb);
      });

      knxRows = knxRows.concat(actorChannelsToRows(button, [...logicChannelsInput, ...logicChannelsOutput]));
    }
  }

  //////////////////////////////////////////////////////////////////////////////

  if (knxRows.length == 0 && button.template) {
    knxRows.push(
      <tr>
        <td colSpan={5}>
          {state.translate(
            "knxButtonTable.noChannelsAvailable",
            "Es sind keine Kanäle vohanden. Bitte erstellen Sie unter dem Reiter Parameter welche",
          )}
        </td>
      </tr>,
    );
  }

  //
  // channels without knxChannel
  //
  if (errorChannels.length > 0) {
    knxRows.push(<UnMappedButtonsHeader />);
    knxRows = knxRows.concat(collectionToRows(button, errorChannels));
  }

  // manual connectet signals/ group adresses
  if (isUiModeExpert()) {
    const manuallyConnectedSignals = new Array<Signal>();
    button.channels.forEach((channel) => {
      if (
        channel.channelType == ChannelType.Special ||
        channel.channelType == ChannelType.ManualSignals ||
        channel.channelType == ChannelType.Ignored ||
        channel.channelType == ChannelType.Unknown
      ) {
        channel.signals.forEach((signal) => {
          let showSignal = false;
          signal.groupAddresses.forEach((groupAddress) => {
            if (groupAddress.isManualGroupAddress) {
              showSignal = true;
            }
          });
          if (showSignal) {
            manuallyConnectedSignals.push(signal);
          }
        });
      }
    });
    if (manuallyConnectedSignals.length > 0) {
      knxRows.push(<HeaderRow text={itx("knxButtonTable.manuallyConnectedSignals", "Manuell verbundene Signale")} />);
      manuallyConnectedSignals.forEach((signal) => {
        knxRows.push(<OneSignal button={button} signal={signal} index={0} redraw={redraw} withDelete={false} />);
      });
    }
  }

  return (
    <div
      ref={rootElementRef}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "relative", flexGrow: "1", flexShrink: "1", overflow: "auto", padding: "10px" }}>
        <table className="buttonButtonList">
          {!button.isDecentralActor && (
            <thead>
              <tr
                style={{
                  color: "var(--knxColorWhite)",
                  backgroundColor: "var(--knxColorGrey)",
                }}
              >
                {elementsHeader}
              </tr>
            </thead>
          )}

          <tbody>{knxRows}</tbody>
        </table>
      </div>

      <div
        style={{
          position: "relative",
          height: "max-content",
          alignItems: "center",
          justifyContent: "flex-end",
          display: "flex",
          flexDirection: "row",
          flexGrow: "0",
          flexShrink: "0",
          backgroundColor: "lightgray",
          borderTop: "5px solid white",
        }}
      >
        {button.channels.sortBy("tag").filter((channel) => !channel.mappedChannel).length > 0 &&
          button.channels.sortBy("tag").filter((channel) => channel.mappedChannel).length > 0 && // only plausible if min one channel is mapped-> don`t leave empty actor behind
          button.template &&
          button.isActor && (
            <button
              className="button"
              style={{ margin: "5px" }}
              onClick={() => {
                (button as Actor).sortOpenChannelsToCopyOfThisActor();
              }}
            >
              {itx("tabGeneralDevice.shiftChannelsToCopy", "Nicht zugeortnete Kanäle zu kopie des Aktors verschieben")}
            </button>
          )}

        {button.isButton && !button.template && <AddButton button={button as Button} />}

        <DeleteAllUnusedButtons button={button as Button} key={`DeleteAllUnusedButtons_row_${i}`} />
      </div>
    </div>
  );
}
